import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  BarChart2, Shield, Layers, CheckSquare, FileText, Activity,
  TrendingUp, TrendingDown, AlertTriangle, RefreshCw, ChevronLeft,
  DollarSign, ArrowRight, Clock, CheckCircle, XCircle, Eye,
  Calendar, Download
} from 'lucide-react';
import { treasuryOpsAPI, cryptoDepositAPI, cryptoWithdrawalAPI, p2pRatesAPI } from '../services/api';

const FMT_UGX  = n => 'UGX ' + Number(n || 0).toLocaleString('en-UG', { maximumFractionDigits: 0 });
const FMT_USDT = n => '₮ '   + Number(n || 0).toLocaleString('en', { maximumFractionDigits: 4 });
const FMT_PCT  = n => Number(n || 0).toFixed(1) + '%';

function StatusBadge({ status }) {
  const map = {
    green:  { bg: 'bg-green-500/20',  text: 'text-green-400',  label: 'Healthy' },
    yellow: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Warning' },
    red:    { bg: 'bg-red-500/20',    text: 'text-red-400',    label: 'Critical' },
  };
  const s = map[status] || map.red;
  return (
    <span className={`px-2 py-1 rounded text-xs font-semibold ${s.bg} ${s.text}`}>{s.label}</span>
  );
}

function Card({ children, className = '' }) {
  return (
    <div className={`bg-[#0D1C35] border border-[#1B61AC]/30 rounded-xl p-5 ${className}`}>
      {children}
    </div>
  );
}

function StatTile({ label, value, sub, icon: Icon, accent = '#00A8DB' }) {
  return (
    <Card className="flex items-start gap-4">
      <div className="p-2 rounded-lg" style={{ background: accent + '22' }}>
        <Icon size={20} style={{ color: accent }} />
      </div>
      <div>
        <div className="text-xs text-gray-400 mb-1">{label}</div>
        <div className="text-lg font-bold text-white">{value}</div>
        {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
      </div>
    </Card>
  );
}

function SectionHeader({ title, onRefresh, loading }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-base font-semibold text-white">{title}</h2>
      {onRefresh && (
        <button onClick={onRefresh} disabled={loading}
          className="flex items-center gap-1.5 text-xs text-[#00A8DB] hover:text-white transition-colors disabled:opacity-50">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      )}
    </div>
  );
}

// ── TAB: Dashboard ────────────────────────────────────────────────────────────
function TabDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await treasuryOpsAPI.getSnapshot();
      setData(res.data);
    } catch (e) { toast.error(e.message || 'Failed to load snapshot'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;
  if (!data) return null;

  const liabUGX  = data.liabilities?.UGX?.total  || 0;
  const liabUSDT = data.liabilities?.USDT?.total || 0;
  const q        = data.pendingQueue || {};
  const t        = data.today        || {};
  const ex       = t.exchange        || {};

  return (
    <div className="space-y-6">
      <SectionHeader title="Real-Time Snapshot" onRefresh={load} loading={loading} />

      <div>
        <p className="text-xs text-gray-500 mb-3">Customer Liabilities</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile label="UGX Owed to Customers" value={FMT_UGX(liabUGX)}
            sub={(data.liabilities?.UGX?.accounts || 0) + ' accounts'} icon={DollarSign} />
          <StatTile label="USDT Owed to Customers" value={FMT_USDT(liabUSDT)}
            sub={(data.liabilities?.USDT?.accounts || 0) + ' accounts'} icon={DollarSign} accent="#F59E0B" />
          <StatTile label="Pending Queue Total" value={q.total || 0}
            sub="items needing review" icon={Clock} accent="#EF4444" />
          <StatTile label="Today's Spread Revenue" value={FMT_UGX(ex.totalSpreadRevenueUGX)}
            sub="buy + sell spread" icon={TrendingUp} accent="#10B981" />
        </div>
      </div>

      <div>
        <p className="text-xs text-gray-500 mb-3">Pending Approval Queue</p>
        <div className="grid grid-cols-3 gap-3">
          <Card className="text-center">
            <div className="text-2xl font-bold text-yellow-400">{q.cryptoDeposits || 0}</div>
            <div className="text-xs text-gray-400 mt-1">Crypto Deposits</div>
          </Card>
          <Card className="text-center">
            <div className="text-2xl font-bold text-orange-400">{q.cryptoWithdrawals || 0}</div>
            <div className="text-xs text-gray-400 mt-1">Crypto Withdrawals</div>
          </Card>
          <Card className="text-center">
            <div className="text-2xl font-bold text-red-400">{q.yoWithdrawals || 0}</div>
            <div className="text-xs text-gray-400 mt-1">Yo! Withdrawals</div>
          </Card>
        </div>
      </div>

      <div>
        <p className="text-xs text-gray-500 mb-3">Today's Exchange Activity</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile label="Buy Volume (UGX)" value={FMT_UGX(ex.buyVolumeUGX)} sub={ex.buyCount + ' trades'} icon={TrendingUp} accent="#00A8DB" />
          <StatTile label="Buy Volume (USDT)" value={FMT_USDT(ex.buyVolumeUSDT)} sub="USDT purchased" icon={TrendingUp} accent="#00A8DB" />
          <StatTile label="Sell Volume (USDT)" value={FMT_USDT(ex.sellVolumeUSDT)} sub={ex.sellCount + ' trades'} icon={TrendingDown} accent="#F59E0B" />
          <StatTile label="Withdrawal Fees" value={FMT_USDT(t.withdrawalFeeRevenue?.usdt || 0)} sub={t.withdrawalFeeRevenue?.count + ' withdrawals'} icon={DollarSign} accent="#10B981" />
        </div>
      </div>

      <p className="text-xs text-gray-500 text-right">Generated: {data.generatedAt ? new Date(data.generatedAt).toLocaleString() : '—'}</p>
    </div>
  );
}

// ── TAB: Float Management ─────────────────────────────────────────────────────
function TabFloat() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await treasuryOpsAPI.getFloat();
      setData(res.data);
    } catch (e) { toast.error(e.message || 'Failed to load float'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;
  if (!data) return null;

  const { floats } = data;
  const totals = floats?.totals || {};

  function FloatRow({ entry, currency }) {
    if (!entry) return (
      <div className="text-xs text-gray-600 italic py-2">Not configured</div>
    );
    return (
      <div className={`flex items-center justify-between py-2.5 border-b border-white/5 last:border-0 ${entry.lowFloatAlert ? 'bg-red-900/20 -mx-3 px-3 rounded' : ''}`}>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white">{entry.name || entry.code}</span>
            {entry.lowFloatAlert && (
              <span className="px-1.5 py-0.5 text-xs font-bold bg-red-500 text-white rounded animate-pulse">
                LOW FLOAT
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500">
            {entry.type}
            {entry.alertThreshold != null && (
              <span className="ml-2 text-gray-600">· alert &lt; {FMT_UGX(entry.alertThreshold)}</span>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className={`text-sm font-semibold ${entry.lowFloatAlert ? 'text-red-400' : 'text-white'}`}>
            {currency === 'UGX' ? FMT_UGX(entry.available) : FMT_USDT(entry.available)}
          </div>
          {entry.reserved > 0 && (
            <div className="text-xs text-yellow-400">+{currency === 'UGX' ? FMT_UGX(entry.reserved) : FMT_USDT(entry.reserved)} reserved</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SectionHeader title="Float Management" onRefresh={load} loading={loading} />

      <div className="grid grid-cols-2 gap-4">
        <StatTile label="Total UGX Float" value={FMT_UGX(totals.ugx?.available || 0)}
          sub={FMT_UGX(totals.ugx?.reserved || 0) + ' reserved'} icon={DollarSign} />
        <StatTile label="Total USDT Float" value={FMT_USDT(totals.usdt?.available || 0)}
          sub={FMT_USDT(totals.usdt?.reserved || 0) + ' reserved'} icon={DollarSign} accent="#F59E0B" />
      </div>

      {floats?.ugx?.yo?.lowFloatAlert && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-900/40 border border-red-500/50 rounded-lg text-sm">
          <span className="text-red-400 font-bold text-base">⚠</span>
          <div>
            <span className="text-red-300 font-semibold">Yo! float critically low — </span>
            <span className="text-red-400">{FMT_UGX(floats.ugx.yo.available)} remaining (threshold: {FMT_UGX(floats.ugx.yo.alertThreshold)}). Payouts will fail when float is exhausted. Top up now via Treasury → Float Top-up.</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <h3 className="text-sm font-semibold text-[#00A8DB] mb-3">UGX Float</h3>
          <FloatRow entry={floats?.ugx?.yo}   currency="UGX" />
          <FloatRow entry={floats?.ugx?.bank} currency="UGX" />
          <FloatRow entry={floats?.ugx?.cash} currency="UGX" />
          {(floats?.ugx?.other || []).map((e, i) => <FloatRow key={i} entry={e} currency="UGX" />)}
        </Card>
        <Card>
          <h3 className="text-sm font-semibold text-yellow-400 mb-3">USDT Float</h3>
          <FloatRow entry={floats?.usdt?.trc20} currency="USDT" />
          <FloatRow entry={floats?.usdt?.bep20} currency="USDT" />
          <FloatRow entry={floats?.usdt?.erc20} currency="USDT" />
          {(floats?.usdt?.other || []).map((e, i) => <FloatRow key={i} entry={e} currency="USDT" />)}
        </Card>
      </div>
    </div>
  );
}

// ── TAB: Crypto Treasury ──────────────────────────────────────────────────────
function TabCryptoWallets() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await treasuryOpsAPI.getCryptoWallets();
      setData(res.data);
    } catch (e) { toast.error(e.message || 'Failed to load crypto wallets'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;
  if (!data) return null;

  const wallets = data.wallets || [];

  return (
    <div className="space-y-4">
      <SectionHeader title="Crypto Treasury Wallets" onRefresh={load} loading={loading} />
      {wallets.length === 0 && (
        <Card><p className="text-gray-400 text-sm text-center py-4">No wallet addresses configured</p></Card>
      )}
      {wallets.map(w => (
        <Card key={w._id}>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-semibold text-white">{w.label || w.address}</span>
                {w.isPrimary && <span className="text-xs bg-[#00A8DB]/20 text-[#00A8DB] px-1.5 py-0.5 rounded">Primary</span>}
                <span className={`text-xs px-1.5 py-0.5 rounded ${w.isActive ? 'bg-green-500/20 text-green-400' : 'bg-gray-600/20 text-gray-400'}`}>
                  {w.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="text-xs text-gray-400 font-mono truncate">{w.address}</div>
              <div className="text-xs text-gray-500 mt-1">{w.network?.name || w.networkCode} · {w.walletType}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-xs text-gray-500">Today</div>
              <div className="text-sm text-green-400">+{FMT_USDT(w.todayDeposits?.total || 0)}</div>
              <div className="text-sm text-red-400">-{FMT_USDT(w.todayWithdrawals?.total || 0)}</div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ── TAB: Approval Queue ───────────────────────────────────────────────────────
function TabApprovalQueue() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await treasuryOpsAPI.getApprovalQueue();
      setData(res.data);
    } catch (e) { toast.error(e.message || 'Failed to load queue'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function approveCryptoDeposit(id) {
    const amt = window.prompt('Credit amount in USDT:');
    if (!amt) return;
    setActionLoading(true);
    try {
      await cryptoDepositAPI.approve(id, { creditAmount: parseFloat(amt) });
      toast.success('Deposit approved and wallet credited');
      load();
    } catch (e) { toast.error(e.message || 'Approve failed'); }
    finally { setActionLoading(false); }
  }

  async function rejectCryptoDeposit(id) {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;
    setActionLoading(true);
    try {
      await cryptoDepositAPI.reject(id, { reason });
      toast.success('Deposit rejected');
      load();
    } catch (e) { toast.error(e.message || 'Reject failed'); }
    finally { setActionLoading(false); }
  }

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;
  if (!data) return null;

  const totalPending = data.total || 0;

  return (
    <div className="space-y-6">
      <SectionHeader title={`Approval Queue (${totalPending} pending)`} onRefresh={load} loading={loading} />

      {/* Crypto Deposits */}
      <div>
        <h3 className="text-sm font-semibold text-yellow-400 mb-3">Crypto Deposits ({data.cryptoDeposits?.count || 0})</h3>
        {(data.cryptoDeposits?.items || []).length === 0
          ? <Card><p className="text-gray-400 text-sm text-center py-3">None pending</p></Card>
          : (data.cryptoDeposits?.items || []).map(dep => (
            <Card key={dep._id} className="mb-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-white">{dep.requestId}</div>
                  <div className="text-xs text-gray-400">{dep.userId?.name} · {dep.userId?.email}</div>
                  <div className="text-xs text-gray-500 mt-1">Network: {dep.networkCode} · Expected: {FMT_USDT(dep.expectedAmount)}</div>
                  {dep.txid && <div className="text-xs font-mono text-gray-600 mt-1 truncate max-w-xs">TXID: {dep.txid}</div>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => approveCryptoDeposit(dep._id)} disabled={actionLoading}
                    className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white text-xs rounded-lg transition-colors disabled:opacity-50">
                    Approve
                  </button>
                  <button onClick={() => rejectCryptoDeposit(dep._id)} disabled={actionLoading}
                    className="px-3 py-1.5 bg-red-700 hover:bg-red-600 text-white text-xs rounded-lg transition-colors disabled:opacity-50">
                    Reject
                  </button>
                </div>
              </div>
            </Card>
          ))
        }
      </div>

      {/* Crypto Withdrawals */}
      <div>
        <h3 className="text-sm font-semibold text-orange-400 mb-3">Crypto Withdrawals ({data.cryptoWithdrawals?.count || 0})</h3>
        {(data.cryptoWithdrawals?.items || []).length === 0
          ? <Card><p className="text-gray-400 text-sm text-center py-3">None pending</p></Card>
          : (data.cryptoWithdrawals?.items || []).map(wd => (
            <Card key={wd._id} className="mb-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-white">{wd.reference || wd._id}</div>
                  <div className="text-xs text-gray-400">{wd.user?.name} · {wd.user?.email}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Network: {wd.network} · Amount: {FMT_USDT(wd.amount)} · Net: {FMT_USDT(wd.netAmount)}
                  </div>
                  <div className="text-xs text-gray-600 font-mono mt-1 truncate max-w-xs">To: {wd.destinationAddress}</div>
                </div>
                <span className={`text-xs px-2 py-1 rounded shrink-0 ${
                  wd.status === 'approved' ? 'bg-blue-500/20 text-blue-400' : 'bg-yellow-500/20 text-yellow-400'
                }`}>{wd.status}</span>
              </div>
            </Card>
          ))
        }
      </div>

      {/* Large UGX Withdrawals */}
      <div>
        <h3 className="text-sm font-semibold text-red-400 mb-3">Large UGX Withdrawals ≥1M ({data.largeYoWithdrawals?.count || 0})</h3>
        {(data.largeYoWithdrawals?.items || []).length === 0
          ? <Card><p className="text-gray-400 text-sm text-center py-3">None pending</p></Card>
          : (data.largeYoWithdrawals?.items || []).map(wd => (
            <Card key={wd._id} className="mb-3">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm font-semibold text-white">{FMT_UGX(wd.amount)}</div>
                  <div className="text-xs text-gray-400">{wd.userId?.name} · {wd.userId?.phone}</div>
                  <div className="text-xs text-gray-500 mt-1">Fee: {FMT_UGX(wd.fee)} · Net: {FMT_UGX(wd.amount - wd.fee)}</div>
                </div>
                <span className="text-xs px-2 py-1 rounded bg-yellow-500/20 text-yellow-400">{wd.status}</span>
              </div>
            </Card>
          ))
        }
      </div>
    </div>
  );
}

// ── TAB: Reports ──────────────────────────────────────────────────────────────
function TabReports() {
  const [period, setPeriod] = useState('daily');
  const [date, setDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await treasuryOpsAPI.getReport(period, date || undefined);
      setData(res.data);
    } catch (e) { toast.error(e.message || 'Failed to load report'); }
    finally { setLoading(false); }
  }, [period, date]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-base font-semibold text-white">Financial Reports</h2>
        <div className="flex items-center gap-2">
          <select value={period} onChange={e => setPeriod(e.target.value)}
            className="bg-[#0D1C35] border border-[#1B61AC]/30 text-white text-sm rounded-lg px-3 py-2">
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="bg-[#0D1C35] border border-[#1B61AC]/30 text-white text-sm rounded-lg px-3 py-2" />
          <button onClick={load} disabled={loading}
            className="px-4 py-2 bg-[#1B61AC] hover:bg-[#00A8DB] text-white text-sm rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Generate
          </button>
        </div>
      </div>

      {loading && <div className="text-center py-12 text-gray-400">Loading...</div>}

      {data && !loading && (
        <div className="space-y-4">
          <Card>
            <p className="text-xs text-gray-400 mb-3">Report Period: <span className="text-white font-medium">{data.label}</span></p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <div className="text-xs text-gray-400">Buy Volume (UGX)</div>
                <div className="text-base font-semibold text-white">{FMT_UGX(data.exchange?.buy?.volumeUGX)}</div>
                <div className="text-xs text-gray-500">{data.exchange?.buy?.count} trades</div>
              </div>
              <div>
                <div className="text-xs text-gray-400">Buy Volume (USDT)</div>
                <div className="text-base font-semibold text-white">{FMT_USDT(data.exchange?.buy?.volumeUSDT)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400">Buy Spread Revenue</div>
                <div className="text-base font-semibold text-green-400">{FMT_UGX(data.exchange?.buy?.spreadRevenueUGX)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400">Sell Volume (USDT)</div>
                <div className="text-base font-semibold text-white">{FMT_USDT(data.exchange?.sell?.volumeUSDT)}</div>
                <div className="text-xs text-gray-500">{data.exchange?.sell?.count} trades</div>
              </div>
              <div>
                <div className="text-xs text-gray-400">Sell Volume (UGX)</div>
                <div className="text-base font-semibold text-white">{FMT_UGX(data.exchange?.sell?.volumeUGX)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400">Sell Spread Revenue</div>
                <div className="text-base font-semibold text-green-400">{FMT_UGX(data.exchange?.sell?.spreadRevenueUGX)}</div>
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <h4 className="text-xs font-semibold text-[#00A8DB] mb-2">Total Spread Revenue</h4>
              <div className="text-xl font-bold text-green-400">{FMT_UGX(data.exchange?.totalSpreadRevenueUGX)}</div>
            </Card>
            <Card>
              <h4 className="text-xs font-semibold text-[#00A8DB] mb-2">Crypto Withdrawal Fees</h4>
              <div className="text-xl font-bold text-white">{FMT_USDT(data.cryptoWithdrawals?.fees)}</div>
              <div className="text-xs text-gray-500 mt-1">{data.cryptoWithdrawals?.count} withdrawals</div>
            </Card>
            <Card>
              <h4 className="text-xs font-semibold text-[#00A8DB] mb-2">UGX Deposits</h4>
              <div className="text-xl font-bold text-white">{FMT_UGX(data.deposits?.UGX?.total || 0)}</div>
              <div className="text-xs text-gray-500 mt-1">{data.deposits?.UGX?.count || 0} transactions</div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

// ── TAB: Reserve Ratio ────────────────────────────────────────────────────────
function TabReserve() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await treasuryOpsAPI.getReserveRatio();
      setData(res.data);
    } catch (e) { toast.error(e.message || 'Failed to load reserve ratio'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;
  if (!data) return null;

  function ReserveBlock({ label, row, fmtFn }) {
    const barW = Math.min(100, row.ratio || 0);
    const barColor = row.status === 'green' ? '#10B981' : row.status === 'yellow' ? '#F59E0B' : '#EF4444';
    return (
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white">{label}</h3>
          <StatusBadge status={row.status} />
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">Customer Liability</span>
            <span className="text-white font-medium">{fmtFn(row.customerLiability)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Actual Reserve</span>
            <span className="text-white font-medium">{fmtFn(row.actualReserve)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Surplus / Deficit</span>
            <span className={row.surplus >= 0 ? 'text-green-400 font-medium' : 'text-red-400 font-medium'}>
              {row.surplus >= 0 ? '+' : ''}{fmtFn(row.surplus)}
            </span>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex justify-between text-xs text-gray-400 mb-1">
            <span>Reserve Coverage</span>
            <span className="font-bold" style={{ color: barColor }}>{FMT_PCT(row.ratio)}</span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: barW + '%', background: barColor }} />
          </div>
          <div className="flex justify-between text-xs text-gray-600 mt-1">
            <span>0%</span><span className="text-yellow-600">80% warn</span><span>100% target</span>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <SectionHeader title="Reserve Ratio — Solvency Monitor" onRefresh={load} loading={loading} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ReserveBlock label="UGX Reserve" row={data.ugx}  fmtFn={FMT_UGX} />
        <ReserveBlock label="USDT Reserve" row={data.usdt} fmtFn={FMT_USDT} />
      </div>
      <p className="text-xs text-gray-500 text-right">Generated: {data.generatedAt ? new Date(data.generatedAt).toLocaleString() : '—'}</p>
    </div>
  );
}

// ── TAB: Audit Log ────────────────────────────────────────────────────────────
function TabAuditLog() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [action, setAction] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await treasuryOpsAPI.getAuditLog({ page, startDate: startDate || undefined, endDate: endDate || undefined, action: action || undefined });
      setData(res.data);
    } catch (e) { toast.error(e.message || 'Failed to load audit log'); }
    finally { setLoading(false); }
  }, [page, startDate, endDate, action]);

  useEffect(() => { load(); }, [load]);

  const logs = data?.logs || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-base font-semibold text-white">Audit Log</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <input placeholder="Filter by action..." value={action} onChange={e => { setAction(e.target.value); setPage(1); }}
            className="bg-[#0D1C35] border border-[#1B61AC]/30 text-white text-sm rounded-lg px-3 py-2 w-36" />
          <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); }}
            className="bg-[#0D1C35] border border-[#1B61AC]/30 text-white text-sm rounded-lg px-3 py-2" />
          <span className="text-gray-500 text-sm">to</span>
          <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); }}
            className="bg-[#0D1C35] border border-[#1B61AC]/30 text-white text-sm rounded-lg px-3 py-2" />
          <button onClick={load} disabled={loading}
            className="px-3 py-2 bg-[#1B61AC] hover:bg-[#00A8DB] text-white text-sm rounded-lg transition-colors disabled:opacity-50">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {loading && <div className="text-center py-12 text-gray-400">Loading...</div>}

      {!loading && logs.length === 0 && (
        <Card><p className="text-center text-gray-400 py-6">No audit entries found</p></Card>
      )}

      {!loading && logs.length > 0 && (
        <div className="space-y-2">
          {logs.map(log => (
            <Card key={log._id} className="py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-white">{log.action}</span>
                    {(log.tags || []).map(t => (
                      <span key={t} className="text-xs bg-[#1B61AC]/30 text-[#00A8DB] px-1.5 py-0.5 rounded">{t}</span>
                    ))}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {log.userId?.name || log.userId?.email || 'System'} · {log.details?.reference || ''}
                  </div>
                  {log.details && (
                    <div className="text-xs text-gray-600 mt-1 truncate">
                      {Object.entries(log.details).slice(0, 3).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-gray-500">{new Date(log.createdAt).toLocaleString()}</div>
                  {log.ipAddress && <div className="text-xs text-gray-600 mt-0.5">{log.ipAddress}</div>}
                </div>
              </div>
            </Card>
          ))}

          {/* Pagination */}
          {data && data.pages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                className="px-3 py-1.5 bg-[#0D1C35] border border-[#1B61AC]/30 text-white text-sm rounded-lg disabled:opacity-30 hover:border-[#00A8DB]">
                Previous
              </button>
              <span className="text-sm text-gray-400">Page {data.page} of {data.pages}</span>
              <button disabled={page >= data.pages} onClick={() => setPage(p => p + 1)}
                className="px-3 py-1.5 bg-[#0D1C35] border border-[#1B61AC]/30 text-white text-sm rounded-lg disabled:opacity-30 hover:border-[#00A8DB]">
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── TAB: P2P Reference Rates ───────────────────────────────────────────────────
// Manages Binance P2P Uganda reference rates, spread tier display, and daily summary.
// Acquisition Rate = top SELL ads (what DWI pays to acquire USDT)
// Disposal Rate    = top BUY ads  (what DWI gets when disposing USDT)
function TabP2PRates() {
  const [snapshot, setSnapshot]           = useState(null);
  const [config, setConfig]               = useState(null);
  const [history, setHistory]             = useState(null);
  const [dailySummary, setDailySummary]   = useState(null);
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState('');
  const [p2pFallback, setP2pFallback]     = useState({ active: false, reason: null });

  // Rate entry state
  const [acqInput, setAcqInput]     = useState('');
  const [acqNote, setAcqNote]       = useState('');
  const [disInput, setDisInput]     = useState('');
  const [disNote, setDisNote]       = useState('');

  // Config editing
  const [cfgEdit, setCfgEdit]       = useState(null);

  // Daily summary manual commission field
  const [commission, setCommission] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [snapRes, cfgRes, histRes, dayRes] = await Promise.all([
        p2pRatesAPI.getCurrent(),
        p2pRatesAPI.getConfig(),
        p2pRatesAPI.getHistory({ limit: 20 }),
        p2pRatesAPI.getDailySummary(),
      ]);
      const snap = snapRes.data;
      setSnapshot(snap);
      setP2pFallback({
        active:       !!snapRes.p2pFallbackActive,
        reason:       snapRes.p2pFallbackReason || null,
        pricingMode:  snapRes.pricingMode || 'manual',
        ratesStale:   !!snapRes.p2pRatesStale,
        ratesAgeHours: snapRes.p2pRatesAgeHours || null,
      });
      setConfig(cfgRes.config);
      setCfgEdit({ ...cfgRes.config });
      setHistory(histRes);
      setDailySummary(dayRes);
      // Pre-fill inputs from current rates
      if (snap.acqRate) setAcqInput(String(snap.acqRate));
      if (snap.disRate) setDisInput(String(snap.disRate));
    } catch (e) { toast.error(e.message || 'Failed to load P2P rates'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveRate = async (type) => {
    const val  = type === 'acquisition' ? acqInput  : disInput;
    const note = type === 'acquisition' ? acqNote   : disNote;
    const rate = parseFloat(val);
    if (!rate || rate < 100) { toast.error('Enter a valid rate (>= 100 UGX)'); return; }
    setSaving(type);
    try {
      await (type === 'acquisition'
        ? p2pRatesAPI.setAcquisition({ rateUGX: rate, note })
        : p2pRatesAPI.setDisposal({ rateUGX: rate, note }));
      toast.success(`${type === 'acquisition' ? 'Fast Acquisition' : 'Fast Disposal'} Rate updated`);
      if (type === 'acquisition') setAcqNote('');
      else setDisNote('');
      await load();
    } catch (e) { toast.error(e.message || 'Save failed'); }
    finally { setSaving(''); }
  };

  const saveCfg = async () => {
    setSaving('cfg');
    try {
      await p2pRatesAPI.updateConfig(cfgEdit);
      toast.success('Settings saved');
      await load();
    } catch (e) { toast.error(e.message || 'Failed to save settings'); }
    finally { setSaving(''); }
  };

  const toggleReviewOverride = async () => {
    setSaving('override');
    try {
      const current = await p2pRatesAPI.getConfig();
      const cur = current.config;
      // P2P_REVIEW_OVERRIDE_ENABLED is stored in config — we update via configAPI
      await p2pRatesAPI.updateConfig({ ...cur, reviewOverride: !cur.reviewOverride });
      toast.success('Exchange override toggled');
      await load();
    } catch (e) { toast.error(e.message || 'Toggle failed'); }
    finally { setSaving(''); }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const UGX = (n) => (n == null ? '—' : 'UGX ' + Number(n).toLocaleString('en-UG', { maximumFractionDigits: 2 }));
  const USDT = (n) => (n == null ? '—' : '₮ ' + Number(n).toLocaleString('en', { maximumFractionDigits: 4 }));
  const ageStr = (mins) => {
    if (mins == null) return 'never';
    if (mins < 60) return `${mins} min ago`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
  };

  const tierColors = {
    green: { bg: 'bg-green-500/15',  border: 'border-green-500/40',  text: 'text-green-400',  badge: 'bg-green-500/30 text-green-300' },
    amber: { bg: 'bg-yellow-500/15', border: 'border-yellow-500/40', text: 'text-yellow-400', badge: 'bg-yellow-500/30 text-yellow-300' },
    red:   { bg: 'bg-red-500/15',    border: 'border-red-500/40',    text: 'text-red-400',    badge: 'bg-red-500/30 text-red-300' },
  };

  if (loading) return <div className="text-center py-12 text-gray-400">Loading P2P rates...</div>;

  const tier    = snapshot?.tier;
  const tc      = tierColors[tier?.color || 'green'];
  const stale   = snapshot?.staleness;
  const s       = dailySummary?.summary;
  const commAmt = parseFloat(commission) || 0;
  const netPL   = s ? s.totalSpreadPL + commAmt : null;

  return (
    <div className="space-y-6">
      <SectionHeader title="P2P Reference Rates" onRefresh={load} loading={loading} />

      {/* ── Pricing mode: manual — staleness warning ──────────────────────────── */}
      {p2pFallback.pricingMode !== 'manual_with_fallback' && p2pFallback.ratesStale && (
        <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/40 rounded-xl p-4">
          <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-300">
              P2P rates are {p2pFallback.ratesAgeHours != null ? `${p2pFallback.ratesAgeHours}h` : 'very'} old — exchange is quoting these rates
            </p>
            <p className="text-xs text-amber-400/80 mt-0.5">
              Update acquisition and disposal rates below to keep prices accurate.
            </p>
          </div>
        </div>
      )}

      {/* ── Pricing mode: manual_with_fallback — CoinGecko fallback active ────── */}
      {p2pFallback.pricingMode === 'manual_with_fallback' && p2pFallback.active && (
        <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/40 rounded-xl p-4">
          <AlertTriangle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-300">
              Exchange running on CoinGecko fallback — P2P rates stale
            </p>
            <p className="text-xs text-red-400/80 mt-0.5">
              {p2pFallback.reason} — Update acquisition and disposal rates below to return to P2P pricing.
            </p>
          </div>
        </div>
      )}

      {/* ── Staleness warning ─────────────────────────────────────────────── */}
      {stale?.isStale && (
        <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/40 rounded-xl p-4">
          <AlertTriangle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-300">
              {stale.neverSet ? 'P2P rates have never been set' : 'P2P rates are stale'}
            </p>
            <p className="text-xs text-red-400/80 mt-0.5">
              {stale.neverSet
                ? 'Enter Binance P2P Uganda reference rates below to activate P2P-based pricing.'
                : `Acquisition: ${ageStr(stale.acquisitionAgeMin)} · Disposal: ${ageStr(stale.disposalAgeMin)} · Threshold: ${stale.thresholdMins} min`}
            </p>
          </div>
        </div>
      )}

      {/* ── Spread tier banner ────────────────────────────────────────────── */}
      {snapshot?.spread != null && tier && (
        <div className={`flex items-center justify-between gap-4 ${tc.bg} border ${tc.border} rounded-xl px-5 py-4`}>
          <div className="flex items-center gap-3">
            <div className={`px-3 py-1.5 rounded-lg text-sm font-bold ${tc.badge}`}>{tier.name}</div>
            <div>
              <p className={`text-sm font-semibold ${tc.text}`}>{tier.label}</p>
              <p className="text-xs text-gray-400 mt-0.5">
                Current spread: {UGX(snapshot.spread)} / USDT
                &nbsp;·&nbsp; GO ≥ {config?.goFloor} &nbsp;·&nbsp; AGG ≥ {config?.aggFloor}
              </p>
            </div>
          </div>
          {tier.requiresConfirmation && (
            <div className="shrink-0 text-right">
              <p className="text-xs text-red-300 mb-1.5">Exchange paused unless confirmed</p>
              <button onClick={toggleReviewOverride} disabled={saving === 'override'}
                className="text-xs px-3 py-1.5 rounded-lg bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30 transition-colors disabled:opacity-50">
                {saving === 'override' ? 'Saving…' : 'Enable exchange during REVIEW'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Current rates + customer prices ──────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Acquisition (BUY from market) */}
        <Card>
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Fast Acquisition Rate</p>
              <p className="text-xs text-gray-600">Top SELL ads on Binance P2P Uganda</p>
            </div>
            <TrendingDown size={16} className="text-[#00A8DB] mt-0.5" />
          </div>
          <div className="text-2xl font-bold text-white mb-1">{UGX(snapshot?.acqRate)}</div>
          <div className="text-xs text-gray-500 mb-4">
            {snapshot?.acquisition
              ? `Updated ${ageStr(stale?.acquisitionAgeMin)} by ${snapshot.acquisition.updatedByName || 'Admin'}`
              : 'Never set'}
          </div>
          <div className="space-y-2">
            <input type="number" value={acqInput} onChange={e => setAcqInput(e.target.value)}
              placeholder="Enter rate (UGX/USDT)"
              className="w-full bg-[#0A1628] border border-[#1B61AC]/40 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-[#00A8DB]" />
            <input type="text" value={acqNote} onChange={e => setAcqNote(e.target.value)}
              placeholder="Note (optional)"
              className="w-full bg-[#0A1628] border border-[#1B61AC]/40 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-[#00A8DB]" />
            <button onClick={() => saveRate('acquisition')} disabled={saving === 'acquisition'}
              className="w-full bg-[#1B61AC] hover:bg-[#2472c8] text-white text-sm font-semibold py-2 rounded-lg transition-colors disabled:opacity-50">
              {saving === 'acquisition' ? 'Saving…' : 'Update Acquisition Rate'}
            </button>
          </div>
          {snapshot?.customerBuyPrice != null && (
            <div className="mt-3 pt-3 border-t border-[#1B61AC]/20">
              <p className="text-xs text-gray-500">Customer BUY price (+ {UGX(config?.buyMarkup)} markup)</p>
              <p className="text-sm font-semibold text-[#00A8DB]">{UGX(snapshot.customerBuyPrice)} / USDT</p>
            </div>
          )}
        </Card>

        {/* Disposal (SELL to market) */}
        <Card>
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Fast Disposal Rate</p>
              <p className="text-xs text-gray-600">Top BUY ads on Binance P2P Uganda</p>
            </div>
            <TrendingUp size={16} className="text-green-400 mt-0.5" />
          </div>
          <div className="text-2xl font-bold text-white mb-1">{UGX(snapshot?.disRate)}</div>
          <div className="text-xs text-gray-500 mb-4">
            {snapshot?.disposal
              ? `Updated ${ageStr(stale?.disposalAgeMin)} by ${snapshot.disposal.updatedByName || 'Admin'}`
              : 'Never set'}
          </div>
          <div className="space-y-2">
            <input type="number" value={disInput} onChange={e => setDisInput(e.target.value)}
              placeholder="Enter rate (UGX/USDT)"
              className="w-full bg-[#0A1628] border border-[#1B61AC]/40 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-[#00A8DB]" />
            <input type="text" value={disNote} onChange={e => setDisNote(e.target.value)}
              placeholder="Note (optional)"
              className="w-full bg-[#0A1628] border border-[#1B61AC]/40 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-[#00A8DB]" />
            <button onClick={() => saveRate('disposal')} disabled={saving === 'disposal'}
              className="w-full bg-[#1B61AC] hover:bg-[#2472c8] text-white text-sm font-semibold py-2 rounded-lg transition-colors disabled:opacity-50">
              {saving === 'disposal' ? 'Saving…' : 'Update Disposal Rate'}
            </button>
          </div>
          {snapshot?.customerSellPrice != null && (
            <div className="mt-3 pt-3 border-t border-[#1B61AC]/20">
              <p className="text-xs text-gray-500">Customer SELL price (- {UGX(config?.sellMarkdown)} markdown)</p>
              <p className="text-sm font-semibold text-green-400">{UGX(snapshot.customerSellPrice)} / USDT</p>
            </div>
          )}
        </Card>
      </div>

      {/* ── Daily Summary ─────────────────────────────────────────────────── */}
      {s && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-white">Daily Summary — {dailySummary.date}</h3>
            <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
              s.currentTier === 'GO' ? 'bg-green-500/20 text-green-400' :
              s.currentTier === 'AGGRESSIVE' ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-red-500/20 text-red-400'
            }`}>{s.currentTier || '—'}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">USDT Bought</p>
              <p className="text-lg font-bold text-[#00A8DB]">{USDT(s.usdtBoughtVolume)}</p>
              <p className="text-xs text-gray-600">{s.buyOrderCount} orders</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">USDT Sold</p>
              <p className="text-lg font-bold text-green-400">{USDT(s.usdtSoldVolume)}</p>
              <p className="text-xs text-gray-600">{s.sellOrderCount} orders</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">Net USDT Position</p>
              <p className={`text-lg font-bold ${s.netUsdtPosition >= 0 ? 'text-white' : 'text-red-400'}`}>
                {USDT(s.netUsdtPosition)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">Spread P&amp;L</p>
              <p className={`text-lg font-bold ${s.totalSpreadPL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {UGX(s.totalSpreadPL)}
              </p>
            </div>
          </div>
          <div className="border-t border-[#1B61AC]/20 pt-4">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="text-xs text-gray-500 block mb-1">Commission Income (manual entry)</label>
                <input type="number" value={commission} onChange={e => setCommission(e.target.value)}
                  placeholder="Enter agent commission income (UGX)"
                  className="w-full bg-[#0A1628] border border-[#1B61AC]/40 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-[#00A8DB]" />
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500 mb-1">Net Profitability</p>
                <p className={`text-xl font-bold ${netPL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {UGX(netPL)}
                </p>
                <p className="text-xs text-gray-600">Spread + Commission</p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* ── Pricing Settings ─────────────────────────────────────────────── */}
      {cfgEdit && (
        <Card>
          <h3 className="text-sm font-semibold text-white mb-4">Pricing Settings</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            {[
              { key: 'buyMarkup',     label: 'Buy Markup (UGX)',           desc: 'Added to acquisition rate for customer BUY price' },
              { key: 'sellMarkdown',  label: 'Sell Markdown (UGX)',        desc: 'Subtracted from disposal rate for customer SELL price' },
              { key: 'stalenessMins', label: 'Staleness Threshold (min)',  desc: 'Show warning if rates older than this' },
              { key: 'deviationPct',  label: 'CoinGecko Deviation Alert (%)', desc: 'Flag if P2P rate deviates from CoinGecko by this %' },
              { key: 'goFloor',       label: 'GO Tier Floor (UGX spread)', desc: 'Spread >= this is GO. Default -22' },
              { key: 'aggFloor',      label: 'AGGRESSIVE Floor (UGX)',     desc: 'Spread >= this is AGGRESSIVE. Below = REVIEW. Default -30' },
            ].map(({ key, label, desc }) => (
              <div key={key}>
                <label className="text-xs text-gray-400 block mb-1">{label}</label>
                <input type="number" value={cfgEdit[key] ?? ''} onChange={e => setCfgEdit(p => ({ ...p, [key]: parseFloat(e.target.value) }))}
                  className="w-full bg-[#0A1628] border border-[#1B61AC]/40 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-[#00A8DB]" />
                <p className="text-xs text-gray-600 mt-0.5">{desc}</p>
              </div>
            ))}
          </div>
          <button onClick={saveCfg} disabled={saving === 'cfg'}
            className="bg-[#1B61AC] hover:bg-[#2472c8] text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors disabled:opacity-50">
            {saving === 'cfg' ? 'Saving…' : 'Save Settings'}
          </button>
        </Card>
      )}

      {/* ── Rate History ──────────────────────────────────────────────────── */}
      {history && (
        <Card>
          <h3 className="text-sm font-semibold text-white mb-3">Rate History (last {history.docs?.length || 0})</h3>
          {!history.docs?.length
            ? <p className="text-sm text-gray-500">No rate changes recorded yet.</p>
            : (
              <div className="space-y-2">
                {history.docs.map(r => (
                  <div key={r._id} className="flex items-center justify-between text-sm border-b border-[#1B61AC]/10 pb-2 last:border-0">
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                        r.rateType === 'acquisition'
                          ? 'bg-[#00A8DB]/20 text-[#00A8DB]'
                          : 'bg-green-500/20 text-green-400'
                      }`}>
                        {r.rateType === 'acquisition' ? 'ACQ' : 'DIS'}
                      </span>
                      <span className="text-white font-semibold">{UGX(r.rateUGX)}</span>
                      {r.note && <span className="text-gray-500 text-xs truncate max-w-[180px]">{r.note}</span>}
                    </div>
                    <div className="text-right text-xs text-gray-500">
                      <div>{r.updatedByName || r.updatedBy?.name || 'Admin'}</div>
                      <div>{new Date(r.createdAt).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            )
          }
        </Card>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
const TABS = [
  { id: 'dashboard',    label: 'Dashboard',       icon: BarChart2 },
  { id: 'float',        label: 'Float',            icon: Layers },
  { id: 'crypto',       label: 'Crypto Treasury',  icon: Shield },
  { id: 'queue',        label: 'Approval Queue',   icon: CheckSquare },
  { id: 'reports',      label: 'Reports',          icon: FileText },
  { id: 'reserve',      label: 'Reserve Ratio',    icon: Activity },
  { id: 'audit',        label: 'Audit Log',        icon: Eye },
  { id: 'p2p',         label: 'P2P Rates',        icon: TrendingUp },
];

export default function TreasuryAdmin() {
  const navigate  = useNavigate();
  const [tab, setTab] = useState('dashboard');

  const user = (() => { try { return JSON.parse(localStorage.getItem('dwi_user') || '{}'); } catch { return {}; } })();
  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center">
        <div className="text-center text-red-400">
          <AlertTriangle size={40} className="mx-auto mb-3" />
          <p className="text-lg font-semibold">Access Denied</p>
          <p className="text-sm text-gray-400 mt-1">Admin only area</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1628] text-[#F0F6FF]">
      {/* Header */}
      <div className="bg-[#0D1C35] border-b border-[#1B61AC]/30 px-4 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <button onClick={() => navigate('/admin')}
            className="text-[#00A8DB] hover:text-white transition-colors">
            <ChevronLeft size={20} />
          </button>
          <div>
            <h1 className="text-lg font-bold text-white">Treasury & Operations Center</h1>
            <p className="text-xs text-gray-400">Phase 28 — Real-time financial monitoring</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tab bar */}
        <div className="flex gap-1 overflow-x-auto pb-1 mb-6 scrollbar-hide">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  tab === t.id
                    ? 'bg-[#1B61AC] text-white'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}>
                <Icon size={14} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        {tab === 'dashboard' && <TabDashboard />}
        {tab === 'float'     && <TabFloat />}
        {tab === 'crypto'    && <TabCryptoWallets />}
        {tab === 'queue'     && <TabApprovalQueue />}
        {tab === 'reports'   && <TabReports />}
        {tab === 'reserve'   && <TabReserve />}
        {tab === 'audit'     && <TabAuditLog />}
        {tab === 'p2p'       && <TabP2PRates />}
      </div>
    </div>
  );
}
